/**
 * frontend/src/screens/APIIntegrationsTab.js  (Phase 12g)
 *
 * Admin → API Integrations (technical). Per PSP company:
 *   - API access on/off
 *   - API keys: issue (full key shown ONCE), revoke
 *   - webhook URL + signing secret (generate / rotate / reveal) + test ping
 * plus the API Log (webhook deliveries) with PSP + status filters and Retry.
 * Business details (contact, address, working agreements) live on PSP Companies.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  getPSPCompanies, updatePSPCompany,
  generatePSPWebhookSecret, revealPSPWebhookSecret, testPSPWebhook,
  createPSPApiKey, revokePSPApiKey, getPSPWebhookDeliveries, retryPSPWebhookDelivery,
} from '../api';
import { Spinner } from '../components/UI';

const card  = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, marginBottom: 16 };
const small = { fontSize: 12, color: 'var(--text2)' };
const fmtDT = (d) => (d ? new Date(d).toLocaleString() : '—');
const errMsg = (e, f) => e?.response?.data?.error || f;

function SecretBox({ title, value, onClose }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginTop: 10, padding: 12, background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <code style={{ flex: 1, fontSize: 12, wordBreak: 'break-all', background: '#fff', padding: '6px 8px', borderRadius: 6, border: '1px solid #e5e7eb' }}>{value}</code>
        <button className="btn btn-sm" onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); }}>{copied ? 'Copied' : 'Copy'}</button>
        <button className="btn btn-sm" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

function IntegrationCard({ c, toast, onChanged, onLogChanged }) {
  const [webhookUrl, setUrl]    = useState(c.webhookUrl || '');
  const [newKey, setNewKey]     = useState(null);
  const [secret, setSecret]     = useState(null);
  const [keyLabel, setKeyLabel] = useState('');
  const [saving, setSaving]     = useState(false);
  useEffect(() => { setUrl(c.webhookUrl || ''); }, [c.webhookUrl]);
  const dirty = (webhookUrl || '') !== (c.webhookUrl || '');

  const run = async (fn, okMsg) => {
    try { const r = await fn(); if (okMsg) toast(okMsg, 'success'); onChanged(); return r; }
    catch (e) { toast(errMsg(e, 'Failed'), 'error'); return null; }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600, fontSize: 15 }}>{c.name}</div>
        {!c.isActive && <span style={{ ...small, color: 'var(--danger)' }}>(inactive company)</span>}
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, marginLeft: 'auto' }}>
          <input type="checkbox" checked={c.apiEnabled}
            onChange={e => run(() => updatePSPCompany(c.id, { apiEnabled: e.target.checked }), e.target.checked ? 'API access enabled' : 'API access disabled')} />
          API access
        </label>
      </div>

      {!c.apiEnabled && <div style={{ ...small, marginTop: 8 }}>API access is off — keys and webhooks are hidden (and paused) until it's turned on.</div>}

      {c.apiEnabled && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 14 }}>
          {/* Webhook */}
          <div>
            <div className="label">Webhook URL (HTTPS)</div>
            <input className="input" value={webhookUrl} onChange={e => setUrl(e.target.value)} placeholder="https://psp.example.com/solidiphy/webhooks" />
            {dirty && (
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <button className="btn btn-primary btn-sm" disabled={saving} onClick={async () => {
                  setSaving(true); await run(() => updatePSPCompany(c.id, { webhookUrl }), 'Webhook URL saved'); setSaving(false);
                }}>{saving ? 'Saving…' : 'Save URL'}</button>
                <button className="btn btn-sm" onClick={() => setUrl(c.webhookUrl || '')}>Cancel</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={small}>Signing secret: {c.hasWebhookSecret ? 'set' : 'not set'}</span>
              <button className="btn btn-sm" onClick={async () => {
                if (c.hasWebhookSecret && !window.confirm('Generate a new secret? The PSP must update their verification to the new one.')) return;
                const r = await run(() => generatePSPWebhookSecret(c.id), 'Secret generated'); if (r) setSecret(r.data.webhookSecret);
              }}>{c.hasWebhookSecret ? 'Rotate' : 'Generate'}</button>
              {c.hasWebhookSecret && (
                <button className="btn btn-sm" onClick={async () => { const r = await run(() => revealPSPWebhookSecret(c.id)); if (r) setSecret(r.data.webhookSecret); }}>Reveal</button>
              )}
              <button className="btn btn-sm" disabled={!c.webhookUrl || !c.hasWebhookSecret} onClick={async () => {
                const r = await run(() => testPSPWebhook(c.id));
                const d = r?.data?.delivery;
                if (d) toast(d.status === 'delivered' ? `Ping delivered (HTTP ${d.lastResponseCode})` : `Ping not delivered: ${d.lastError || 'pending'}`, d.status === 'delivered' ? 'success' : 'error');
                onLogChanged();
              }}>Send test ping</button>
            </div>
            {secret && <SecretBox title="Webhook signing secret — give this to the PSP" value={secret} onClose={() => setSecret(null)} />}
          </div>

          {/* API keys */}
          <div>
            <div className="label">API keys</div>
            {c.apiKeys.length === 0 && <div style={{ ...small, fontStyle: 'italic', marginBottom: 6 }}>No active keys</div>}
            {c.apiKeys.map(k => (
              <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '0.5px solid var(--border)' }}>
                <code>{k.keyPrefix}…</code>
                <span style={{ color: 'var(--text2)' }}>{k.label}</span>
                <span style={{ color: 'var(--text3)', marginLeft: 'auto' }}>last used {fmtDT(k.lastUsedAt)}</span>
                <button className="btn btn-sm" style={{ background: '#FEE2E2', color: '#991B1B', border: 'none' }}
                  onClick={() => window.confirm(`Revoke key ${k.keyPrefix}…? Calls using it will fail immediately.`) && run(() => revokePSPApiKey(k.id), 'Key revoked')}>
                  Revoke
                </button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input className="input" value={keyLabel} onChange={e => setKeyLabel(e.target.value)} placeholder="Label (optional)" style={{ fontSize: 12 }} />
              <button className="btn btn-sm btn-primary" onClick={async () => {
                const r = await run(() => createPSPApiKey(c.id, keyLabel), 'Key issued');
                if (r) { setNewKey(r.data.apiKey); setKeyLabel(''); }
              }}>Issue key</button>
            </div>
            {newKey && <SecretBox title="API key — shown only once. Copy it now and send it to the PSP securely." value={newKey} onClose={() => setNewKey(null)} />}
          </div>
        </div>
      )}
    </div>
  );
}

function ApiLog({ companies, toast, refreshKey }) {
  const [rows, setRows]     = useState(null);
  const [psp, setPsp]       = useState('');
  const [status, setStatus] = useState('');
  const load = useCallback(() => {
    const params = {};
    if (psp) params.pspCompanyId = psp;
    if (status) params.status = status;
    getPSPWebhookDeliveries(params).then(r => setRows(r.data)).catch(() => setRows([]));
  }, [psp, status]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const color = { delivered: '#065F46', pending: '#92400E', failed: '#991B1B' };
  return (
    <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600 }}>API Log — webhook deliveries</div>
        <select className="input" value={psp} onChange={e => setPsp(e.target.value)} style={{ width: 170, fontSize: 13, marginLeft: 'auto' }}>
          <option value="">All PSPs</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="input" value={status} onChange={e => setStatus(e.target.value)} style={{ width: 130, fontSize: 13 }}>
          <option value="">All statuses</option>
          <option value="delivered">Delivered</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        <button className="btn btn-sm" onClick={load}>Refresh</button>
      </div>
      {!rows ? <div style={{ padding: 24, textAlign: 'center' }}><Spinner size={16} /></div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg)', textAlign: 'left', color: 'var(--text2)' }}>
                {['Time', 'PSP', 'Event', 'Item', 'Status', 'Attempts', 'Last result', ''].map(h => <th key={h} style={{ padding: '8px 12px', fontWeight: 500 }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: 'var(--text3)' }}>No webhook deliveries</td></tr>
              ) : rows.map(d => (
                <tr key={d.id} style={{ borderTop: '0.5px solid var(--border)' }}>
                  <td style={{ padding: '6px 12px', whiteSpace: 'nowrap' }}>{fmtDT(d.createdAt)}</td>
                  <td style={{ padding: '6px 12px' }}>{d.pspCompany?.name}</td>
                  <td style={{ padding: '6px 12px' }}><code>{d.event}</code></td>
                  <td style={{ padding: '6px 12px' }}>{d.itemId || '—'}</td>
                  <td style={{ padding: '6px 12px', color: color[d.status], fontWeight: 600 }}>{d.status}</td>
                  <td style={{ padding: '6px 12px' }}>{d.attempts}</td>
                  <td style={{ padding: '6px 12px', color: 'var(--text2)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.lastResponseCode ? `HTTP ${d.lastResponseCode}` : ''}{d.lastError ? ` ${d.lastError}` : ''}
                  </td>
                  <td style={{ padding: '6px 12px', textAlign: 'right' }}>
                    {d.status !== 'delivered' && (
                      <button className="btn btn-sm" onClick={async () => {
                        try { await retryPSPWebhookDelivery(d.id); toast('Retried', 'success'); load(); }
                        catch (e) { toast(errMsg(e, 'Retry failed'), 'error'); }
                      }}>Retry</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function APIIntegrationsTab({ toast }) {
  const [companies, setCompanies] = useState(null);
  const [logKey, setLogKey]       = useState(0);
  const load = () => getPSPCompanies().then(r => setCompanies(r.data)).catch(() => { setCompanies([]); toast('Failed to load PSP companies', 'error'); });
  useEffect(() => { load(); }, []);

  if (!companies) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>;
  return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>API Integrations</div>
      <div style={{ ...small, marginBottom: 16 }}>Technical setup for partners that connect through the PSP Partner API. Business details are on PSP Companies.</div>
      {companies.length === 0 && <div style={{ ...card, textAlign: 'center', color: 'var(--text3)' }}>No PSP companies yet — add one on PSP Companies</div>}
      {companies.map(c => <IntegrationCard key={c.id} c={c} toast={toast} onChanged={load} onLogChanged={() => setLogKey(k => k + 1)} />)}
      <ApiLog companies={companies} toast={toast} refreshKey={logKey} />
    </div>
  );
}
